import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Tag, 
  Space, 
  Row, 
  Col, 
  Statistic, 
  message, 
  Spin,
  Tooltip,
  Typography,
  Empty
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  BankOutlined
} from '@ant-design/icons';
import Layout from '../../layout/Layout';
import fetchData from '../../functions/fetchData';
import { getHeaders } from '../../utility/getHeader';
import axios from 'axios';

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

const EmployeeDepartments = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const data = await fetchData('employee_departments', null, 'id', {});
      setDepartments(data || []);
    } catch (error) {
      message.error('Failed to fetch departments');
      console.error('Error fetching departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingDepartment) {
        await axios.put(`/updatecommondata/employee_departments/id/${editingDepartment.id}`, {
          department_name: values.department_name,
          department_code: values.department_code,
          description: values.description || '',
          is_active: values.status === 'active'
        }, getHeaders());
        message.success('Department updated successfully!');
      } else {
        await axios.post('/insertdata/employee_departments', {
          department_name: values.department_name,
          department_code: values.department_code,
          description: values.description || '',
          is_active: values.status === 'active'
        }, getHeaders());
        message.success('Department created successfully!');
      }
      
      setShowModal(false);
      setEditingDepartment(null);
      form.resetFields();
      fetchDepartments();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      
      console.error('Error saving department:', error);
      let errorMessage = 'Failed to save department';
      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid department data. Please check your inputs.';
            break;
          case 401:
            errorMessage = 'Session expired. Please login again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            break;
          case 409:
            errorMessage = 'Department name already exists. Please use a different name.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = error.response.data?.message || 'Failed to save department';
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      message.error(errorMessage);
    }
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    form.setFieldsValue({
      department_name: department.department_name,
      department_code: department.department_code || '',
      description: department.description || '',
      status: department.is_active ? 'active' : 'inactive'
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Department',
      content: 'Are you sure you want to delete this department?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      async onOk() {
        try {
          await axios.delete(`/deletebyid/employee_departments/id/${id}`, getHeaders());
          message.success('Department deleted successfully!');
          fetchDepartments();
        } catch (error) {
          console.error('Error deleting department:', error);
          
          let errorMessage = 'Failed to delete department';
          if (error.response) {
            switch (error.response.status) {
              case 400:
                errorMessage = 'Cannot delete department. It may be linked to employees or positions.';
                break;
              case 401:
                errorMessage = 'Session expired. Please login again.';
                break;
              case 403:
                errorMessage = 'You do not have permission to delete departments.';
                break;
              case 404:
                errorMessage = 'Department not found.';
                break;
              case 500:
                errorMessage = 'Server error. Please try again later.';
                break;
              default:
                errorMessage = error.response.data?.message || 'Failed to delete department';
            }
          } else if (error.request) {
            errorMessage = 'Network error. Please check your connection.';
          }
          
          message.error(errorMessage);
        }
      }
    });
  };

  const filteredDepartments = departments.filter(dept =>
    dept.department_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (dept.description && dept.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Auto-generate department code from department name
  const generateDepartmentCode = (departmentName) => {
    if (!departmentName || departmentName.trim() === '') return '';
    
    const words = departmentName.trim().split(/\s+/);
    let code = '';
    
    if (words.length === 1) {
      code = words[0].substring(0, 4).toUpperCase();
    } else {
      code = words.map(word => word.charAt(0).toUpperCase()).join('');
    }
    
    const randomNum = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    return code + randomNum;
  };

  const handleDepartmentNameChange = (e) => {
    const newName = e.target.value;
    form.setFieldsValue({ department_name: newName });
    
    if (!editingDepartment) {
      const generatedCode = generateDepartmentCode(newName);
      form.setFieldsValue({ department_code: generatedCode });
    }
  };

  // Table columns definition
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: 'Department Name',
      dataIndex: 'department_name',
      key: 'department_name',
      sorter: (a, b) => a.department_name.localeCompare(b.department_name),
    },
    {
      title: 'Code',
      dataIndex: 'department_code',
      key: 'department_code',
      width: 120,
      render: (code) => <Tag color="blue">{code || 'N/A'}</Tag>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || 'No description',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      align: 'center',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <div style={{ padding: '24px' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>
            <BankOutlined /> Department Management
          </Title>
        </div>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12}>
            <Card>
              <Statistic
                title="Total Departments"
                value={loading ? '...' : departments.length}
                prefix={<BankOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card>
              <Statistic
                title="Active Departments"
                value={loading ? '...' : departments.filter(d => d.is_active).length}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Content Card */}
        <Card>
          {/* Toolbar */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            <Col xs={24} sm={12} md={16}>
              <Input
                placeholder="Search departments..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingDepartment(null);
                  form.resetFields();
                  form.setFieldsValue({ status: 'active' });
                  setShowModal(true);
                }}
              >
                Add Department
              </Button>
            </Col>
          </Row>

          {/* Table View */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" tip="Loading departments..." />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredDepartments}
              rowKey="id"
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} departments`,
              }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No departments found"
                  />
                ),
              }}
            />
          )}
        </Card>

        {/* Add/Edit Modal */}
        <Modal
          title={
            <Space>
              <BankOutlined />
              {editingDepartment ? 'Edit Department' : 'Add New Department'}
            </Space>
          }
          open={showModal}
          onOk={handleSubmit}
          onCancel={() => {
            setShowModal(false);
            setEditingDepartment(null);
            form.resetFields();
          }}
          okText={editingDepartment ? 'Update' : 'Create'}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{ status: 'active' }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="department_name"
                  label="Department Name"
                  rules={[
                    { required: true, message: 'Please enter department name' },
                    { min: 2, message: 'Department name must be at least 2 characters' }
                  ]}
                >
                  <Input 
                    placeholder="Enter department name" 
                    onChange={handleDepartmentNameChange}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="department_code"
                  label="Department Code (Auto-generated)"
                  rules={[
                    { required: true, message: 'Please enter department code' },
                    { min: 2, message: 'Department code must be at least 2 characters' }
                  ]}
                >
                  <Input
                    placeholder="Auto-generated from department name"
                    onChange={(e) => {
                      form.setFieldsValue({ department_code: e.target.value.toUpperCase() });
                    }}
                    suffix={
                      <Tooltip title="Auto-generated from department name. You can edit it if needed.">
                        <FileTextOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                      </Tooltip>
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea
                rows={4}
                placeholder="Enter department description"
              />
            </Form.Item>
            <Form.Item
              name="status"
              label="Status"
            >
              <Select>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Layout>
  );
};

export default EmployeeDepartments;
